/**
 * TourManager - Simple tour and highlight management for Magic Mode parlor tricks
 */

class TourManager {
  constructor() {
    this.currentTour = null;
    this.currentStep = 0;
    this.isActive = false;
    this.actionHandlers = new Map();
    this.highlightTimeouts = new Set();
  }

  // Register action handlers from components
  registerActionHandler(actionType, handler) {
    this.actionHandlers.set(actionType, handler);
    // console.log(`🎭 [TourManager] Registered action handler: ${actionType}`);
  }

  // Unregister action handlers (cleanup)
  unregisterActionHandler(actionType) {
    this.actionHandlers.delete(actionType);
    // console.log(`🎭 [TourManager] Unregistered action handler: ${actionType}`);
  }

  // Execute a tour action
  executeAction(action, params = {}) {
    // console.log(`🎭 [TourManager] Executing action:`, action, params);

    switch (action) {
      case 'highlight_rows':
        this.highlightTableRows(params.rows || []);
        break;
      case 'clear_highlights':
        this.clearAllHighlights();
        break;
      case 'sort_table':
        this.executeTableAction('sort', params);
        break;
      case 'apply_filter':
        this.executeTableAction('filter', params);
        break;
      case 'navigate_to_section':
        this.navigateToSection(params.section);
        break;
      case 'navigate_to_analysis':
        // Handle navigation during tours
        const navigationHandler = this.actionHandlers.get('navigation');
        if (navigationHandler) {
          navigationHandler({
            action: 'navigate_to_analysis',
            data: {
              reportType: params.route === '/keywords' ? 'keywords' : 'pages',
              filters: params.filters
            },
            rationale: params.rationale
          });
        } else {
          console.warn('🎭 [TourManager] No navigation handler registered');
        }
        break;
      case 'navigate_to_page':
        // Handle page navigation during tours
        const pageNavigationHandler = this.actionHandlers.get('navigation');
        if (pageNavigationHandler) {
          // Try to pick a page from current table view or use fallback
          const selectedPageUrl = this.selectPageFromCurrentView() || 'https://example.com/sample-page';
          
          pageNavigationHandler({
            action: 'navigate_to_page',
            data: {
              page_url: selectedPageUrl,
              tab: params.tab || 'overview'
            },
            rationale: params.rationale
          });
        } else {
          console.warn('🎭 [TourManager] No navigation handler registered for page');
        }
        break;
      case 'navigate_to_overview':
        // Handle overview navigation using React router
        const overviewHandler = this.actionHandlers.get('navigation');
        if (overviewHandler) {
          overviewHandler({
            action: 'navigate_to_overview',
            data: {},
            rationale: params.rationale
          });
        } else {
          console.warn('🎭 [TourManager] No navigation handler for overview');
        }
        break;
      case 'clear_filters':
        // Clear analysis filters to reset view after tour
        // console.log('🎭 [TourManager] Clearing analysis filters');
        try {
          const { clearAnalysisFiltersOnly } = require('../components2/analysis/utils/analysisFilters');
          clearAnalysisFiltersOnly();
        } catch (error) {
          console.warn('🎭 [TourManager] Failed to clear filters:', error);
        }
        break;
      case 'llm_handoff':
        // Handle LLM handoff - trigger silent assessment
        // console.log('🎭 [TourManager] LLM handoff requested:', params);
        const llmHandler = this.actionHandlers.get('navigation');
        if (llmHandler) {
          llmHandler({
            action: 'llm_handoff',
            data: params,
            rationale: params.context
          });
        } else {
          console.warn('🎭 [TourManager] No navigation handler for LLM handoff');
        }
        break;
      default:
        // Try to find a registered handler
        const handler = this.actionHandlers.get(action);
        if (handler) {
          handler(params);
        } else {
          console.warn(`🎭 [TourManager] Unknown action: ${action}`);
        }
    }
  }

  // Highlight specific table rows
  highlightTableRows(rowIds, duration = 10000) {
    // console.log(`🎭 [TourManager] Highlighting rows:`, rowIds);
    
    // Get the analysis list component handler
    const tableHandler = this.actionHandlers.get('table_actions');
    if (tableHandler) {
      tableHandler({ action: 'highlight_rows', rows: rowIds });
      
      // Auto-clear highlights after duration
      const timeoutId = setTimeout(() => {
        this.clearAllHighlights();
      }, duration);
      
      this.highlightTimeouts.add(timeoutId);
    } else {
      console.warn('🎭 [TourManager] No table handler registered for row highlighting');
    }
  }

  // Clear all highlights
  clearAllHighlights() {
    // console.log('🎭 [TourManager] Clearing all highlights');
    
    // Clear any pending timeouts
    this.highlightTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.highlightTimeouts.clear();
    
    // Clear table row highlights
    const tableHandler = this.actionHandlers.get('table_actions');
    if (tableHandler) {
      tableHandler({ action: 'clear_highlights' });
    }
  }

  // Highlight random rows to simulate agent examining data
  highlightRandomRows(count = 3, duration = 2500) {
    // console.log('🎭 [TourManager] Highlighting random rows for examination');
    
    // Generate random row indices (0-based, typically first 10 rows are visible)
    const availableRows = Array.from({ length: 10 }, (_, i) => i);
    const selectedRows = [];
    
    // Randomly select rows without replacement
    for (let i = 0; i < Math.min(count, availableRows.length); i++) {
      const randomIndex = Math.floor(Math.random() * availableRows.length);
      selectedRows.push(availableRows[randomIndex]);
      availableRows.splice(randomIndex, 1);
    }
    
    // Highlight the selected rows
    this.highlightTableRows(selectedRows, duration);
  }

  // Execute table-specific actions
  executeTableAction(actionType, params) {
    const tableHandler = this.actionHandlers.get('table_actions');
    if (tableHandler) {
      tableHandler({ action: actionType, ...params });
    } else {
      console.warn(`🎭 [TourManager] No table handler for action: ${actionType}`);
    }
  }

  // Navigate to a specific section
  navigateToSection(section) {
    // console.log(`🎭 [TourManager] Navigating to section: ${section}`);
    
    // Add navigation logic here based on section
    const element = document.querySelector(`[data-section="${section}"]`) || 
                   document.querySelector(`#${section}`) ||
                   document.querySelector(`.${section}`);
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Start a full tour sequence
  startTour(tourType, data = {}) {
    // console.log(`🎭 [TourManager] Starting tour: ${tourType}`, data);
    
    const tourConfig = this.getTourConfig(tourType);
    if (!tourConfig) {
      console.warn(`🎭 [TourManager] Unknown tour type: ${tourType}`);
      return;
    }

    this.currentTour = tourType;
    this.currentStep = 0;
    this.isActive = true;
    this.tourData = data;
    
    // Start the first step
    this.executeStep(0, tourConfig);
  }

  // Execute a specific tour step
  executeStep(stepIndex, tourConfig) {
    const steps = tourConfig.steps || [];
    if (stepIndex >= steps.length) {
      // console.log('🎭 [TourManager] Tour completed');
      this.stopTour();
      return;
    }

    const step = steps[stepIndex];
    // console.log(`🎭 [TourManager] Executing step ${stepIndex + 1}/${steps.length}:`, step.message);

    // Clear any existing highlights before starting new step
    this.clearAllHighlights();

    // Handle silent steps (no message to show)
    const chatHandler = this.actionHandlers.get('chat_messaging');
    if (chatHandler && step.message) {
      // Start typing
      chatHandler({
        action: 'start_typing'
      });

      // Send message after typing delay
      setTimeout(() => {
        chatHandler({
          action: 'send_tour_message',
          message: step.message,
          stepIndex: stepIndex,
          totalSteps: steps.length,
          tourType: this.currentTour
        });

        // Execute action after message delay
        setTimeout(() => {
          if (this.isActive && this.currentStep === stepIndex) {
            this.executeAction(step.action, step.params || {});
            
            // Add row highlighting after navigation
            setTimeout(() => {
              if (this.isActive) {
                // Vary the number of highlighted rows and timing for natural feel
                const rowCount = 2 + Math.floor(Math.random() * 3); // 2-4 rows
                const duration = 2000 + Math.random() * 1000; // 2-3 seconds
                this.highlightRandomRows(rowCount, duration);
              }
            }, 1200 + Math.random() * 600); // Slight timing variation (1.2-1.8s)
            
            // Auto-advance to next step after action delay
            setTimeout(() => {
              if (this.isActive) {
                this.currentStep++;
                this.executeStep(this.currentStep, tourConfig);
              }
            }, step.nextStepDelay || 3000);
          }
                          }, step.delay || 1000);

                }, 1000); // Typing duration
    } else if (!step.message) {
      // Silent step - execute action immediately without UI message
      // console.log(`🎭 [TourManager] Silent step ${stepIndex + 1} - executing action directly`);
      setTimeout(() => {
        if (this.isActive && this.currentStep === stepIndex) {
          this.executeAction(step.action, step.params || {});
          
          // Auto-advance to next step after action delay
          setTimeout(() => {
            if (this.isActive) {
              this.currentStep++;
              this.executeStep(this.currentStep, tourConfig);
            }
          }, step.nextStepDelay || 1000);
        }
      }, step.delay || 1000);
    }
  }

  // Get tour configuration - click drops
  getTourConfig(tourType) {
    const tours = {
      onboarding: {
        name: "Onboarding Product Tour",
        description: "5-step guided tour after domain analysis completion",
        steps: [
          {
            message: "Let me check your top-performing pages first to understand what's working well.",
            action: 'navigate_to_analysis',
            params: {
              route: '/analysis',
              filters: { sortType: 'clicks_desc' },
              rationale: 'Analyzing top-performing pages for baseline assessment'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: "Interesting... now I want to see which pages might be losing ground - this could reveal urgent opportunities.",
            action: 'navigate_to_analysis',
            params: {
              route: '/analysis',
              filters: { pageReportFilter: 'clickDrops' },
              rationale: 'Investigating declining pages for quick wins'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: "I need to take a closer look at one of these pages... let me dive into the details.",
            action: 'navigate_to_page',
            params: {
              source: 'analysis_data', // Will pick from current analysis
              tab: 'overview',
              rationale: 'Deep diving into individual page performance'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: "Now let me check your keyword landscape... I'm particularly interested in page 2 opportunities.",
            action: 'navigate_to_analysis',
            params: {
              route: '/keywords',
              filters: { pageReportFilter: 'page2Pages' },
              rationale: 'Exploring page 2 keyword opportunities for growth potential'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: "Let me also look at your click-through rates... searching for high visibility keywords with low engagement.",
            action: 'navigate_to_analysis',
            params: {
              route: '/keywords',
              filters: { pageReportFilter: 'position1LowCTR' },
              rationale: 'Analyzing CTR optimization opportunities'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: "Perfect! Now let me pull everything together in your overview to give you my assessment.",
            action: 'navigate_to_overview',
            params: {
              rationale: 'Reviewing complete picture for final assessment'
            },
            delay: 1000,
            nextStepDelay: 2000
          },
          {
            message: null, // Silent filter reset - clear tour filters before handoff
            action: 'clear_filters',
            params: {},
            delay: 500,
            nextStepDelay: 500
          },
          {
            message: null, // Silent LLM handoff - no message to user
            action: 'llm_handoff',
            params: {
              context: 'tour_completion',
              silentPrompt: 'conduct initial assessment after new user onboard flow'
            },
            delay: 1000,
            nextStepDelay: 700
          }
        ]
      }
    };

    return tours[tourType];
  }

  // Stop current tour
  stopTour() {
    // console.log('🎭 [TourManager] Stopping tour');
    this.isActive = false;
    this.currentTour = null;
    this.currentStep = 0;
    this.clearAllHighlights(); // Ensure highlights are cleared on stop
    
    // Notify ChatSidebar that tour is stopped
    const chatHandler = this.actionHandlers.get('chat_messaging');
    if (chatHandler) {
      chatHandler({ action: 'stop_typing' }); // Stop typing indicator
      chatHandler({ action: 'tour_stopped' }); // Hide tour controls
    }
  }

  // Check if a tour is active
  isTourActive() {
    return this.isActive;
  }

  // Get current tour info
  getCurrentTour() {
    return {
      type: this.currentTour,
      step: this.currentStep,
      active: this.isActive
    };
  }

  // Select a page from the current table view for navigation
  selectPageFromCurrentView() {
    try {
      // Look for page URL elements with the tour-page-url class
      const pageUrlElements = document.querySelectorAll('.tour-page-url[data-page-url]');
      
      if (pageUrlElements.length === 0) {
        // console.log('🎭 [TourManager] No page URL elements found in current view');
        return null;
      }

      // Get visible elements only (filter out hidden/mobile duplicates)
      const visibleElements = Array.from(pageUrlElements).filter(element => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

      if (visibleElements.length === 0) {
        // console.log('🎭 [TourManager] No visible page URL elements found');
        return null;
      }

      // Pick a random URL from the first few visible elements (max 5)
      const elementsToChooseFrom = visibleElements.slice(0, 5);
      const randomIndex = Math.floor(Math.random() * elementsToChooseFrom.length);
      const selectedElement = elementsToChooseFrom[randomIndex];
      const selectedUrl = selectedElement.getAttribute('data-page-url');
      
      if (selectedUrl) {
        // console.log('🎭 [TourManager] Selected page for navigation:', selectedUrl);
        return selectedUrl;
      }

      // console.log('🎭 [TourManager] No valid URL found in selected element');
      return null;

    } catch (error) {
      console.error('🎭 [TourManager] Error selecting page from table:', error);
      return null;
    }
  }
}

// Export singleton instance
export const tourManager = new TourManager();

// Export class for testing
export { TourManager };

// Expose to window for debugging and testing
if (typeof window !== 'undefined') {
  window.tourManager = tourManager;
  
  // Helper function to start onboarding tour for testing
  window.startOnboardingTour = (data = {}) => {
    // console.log('🎭 Starting onboarding tour...');
    tourManager.startTour('onboarding', data);
  };
}
